import React, { useEffect, useState, useRef } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { ddmmmyy } from "@/lib/format";
import { Upload as UploadIcon, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";

const Settings = () => {
  const [settings, setSettings] = useState({ match_pct: 0.5, minor_pct: 2.0 });
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", role: "viewer", full_name: "" });
  const [resetPw, setResetPw] = useState({});
  const [master, setMaster] = useState(null);
  const [uploadingMaster, setUploadingMaster] = useState(false);
  const [editedParts, setEditedParts] = useState(null);   // null means "no local edits"
  const [bulkTariff, setBulkTariff] = useState("");
  const [savingParts, setSavingParts] = useState(false);
  const masterRef = useRef();

  const reload = async () => {
    const [s, u, m] = await Promise.all([
      apiClient.get("/settings"),
      apiClient.get("/users"),
      apiClient.get("/parts-master").catch(() => ({ data: null })),
    ]);
    setSettings({
      match_pct: s.data?.match_pct ?? 0.5,
      minor_pct: s.data?.minor_pct ?? 2.0,
    });
    setUsers(u.data || []);
    setMaster(m.data);
    setEditedParts(null);
  };

  useEffect(() => { reload(); }, []);

  const uploadMaster = async (file) => {
    if (!file) return;
    setUploadingMaster(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiClient.post("/parts-master/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Parts-Tariff Master saved · ${data.count} parts`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingMaster(false);
      if (masterRef.current) masterRef.current.value = "";
    }
  };

  const parts = editedParts ?? master?.parts ?? [];
  const dirty = editedParts !== null;
  const missingDuty = parts.filter((p) => !Number(p.duty_per_unit)).length;

  const editPart = (idx, field, value) => {
    const next = parts.map((p, i) => i === idx ? { ...p, [field]: value } : p);
    setEditedParts(next);
  };

  const saveParts = async () => {
    setSavingParts(true);
    try {
      const payload = parts.map((p) => ({
        part_number: p.part_number || "",
        hts_code: p.hts_code || "",
        duty_per_unit: Number(p.duty_per_unit) || 0,
        tariff_pct: p.tariff_pct == null || p.tariff_pct === "" ? null : Number(p.tariff_pct),
        freight_per_unit: p.freight_per_unit == null || p.freight_per_unit === "" ? null : Number(p.freight_per_unit),
        unit_price: p.unit_price == null || p.unit_price === "" ? null : Number(p.unit_price),
        description: p.description || "",
      }));
      await apiClient.put("/parts-master/parts", { parts: payload });
      toast.success("Master parts updated");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingParts(false);
    }
  };

  const applyTariff = async () => {
    const v = parseFloat(bulkTariff);
    if (!v || v <= 0) return toast.error("Enter a positive tariff %");
    try {
      const { data } = await apiClient.post("/parts-master/apply-tariff", {
        tariff_pct: v,
        only_missing: false,
      });
      toast.success(`Applied ${v}% to ${data.updated} parts`);
      setBulkTariff("");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const saveThresholds = async () => {
    try {
      await apiClient.put("/settings/thresholds", settings);
      toast.success("Thresholds saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/users", form);
      toast.success(`User ${form.username} created`);
      setForm({ username: "", password: "", role: "viewer", full_name: "" });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const toggleUser = async (id) => {
    try {
      await apiClient.post(`/users/${id}/toggle`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const doReset = async (id) => {
    const pw = resetPw[id];
    if (!pw || pw.length < 8) return toast.error("Password ≥ 8 chars");
    try {
      await apiClient.post("/users/reset-password", { user_id: id, new_password: pw });
      toast.success("Password reset");
      setResetPw({ ...resetPw, [id]: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Configuration</div>
        <h1 className="text-2xl font-bold text-[#0F2044]">Settings</h1>
      </div>

      {/* Parts-Tariff Master */}
      <div className="bg-white rounded-md p-5 mb-6" style={{ border: "1px solid var(--color-border)" }} data-testid="parts-master-card">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[#0F2044]">
              Parts-Tariff Master
            </div>
            <div className="text-xs text-gray-500 mt-1">
              The single source of truth for HTS code, tariff %, and freight per unit. Every shipment uses the latest master.
            </div>
          </div>
          {master?.configured ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
              <CheckCircle2 size={14} /> Configured
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
              <AlertCircle size={14} /> Not configured
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            {master?.configured ? (
              <div className="flex items-center gap-3 p-3 rounded-md" style={{ background: "#F8F9FC", border: "1px solid var(--color-border)" }}>
                <FileSpreadsheet className="text-[#0F2044]" size={20} />
                <div className="text-sm">
                  <div className="font-semibold text-[#0F2044]">{master.original_filename}</div>
                  <div className="text-xs text-gray-500">
                    <span data-testid="master-count">{master.count}</span> parts ·
                    uploaded <span className="uppercase-date">{ddmmmyy(master.uploaded_at)}</span>
                    · by <span className="font-mono">{master.uploaded_by}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 p-3 rounded-md" style={{ background: "#F8F9FC", border: "1px dashed var(--color-border)" }}>
                No master uploaded yet. Upload an Excel file with columns: part_number, hts_code, tariff_pct, freight_per_unit, unit_price.
              </div>
            )}
          </div>
          <div>
            <input
              ref={masterRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="master-input"
              onChange={(e) => uploadMaster(e.target.files?.[0])}
            />
            <button
              data-testid="upload-master-btn"
              disabled={uploadingMaster}
              onClick={() => masterRef.current?.click()}
              className="w-full bg-[#0F2044] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#1a2f5c] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <UploadIcon size={14} />
              {uploadingMaster ? "Uploading…" : master?.configured ? "Replace master" : "Upload master"}
            </button>
          </div>
        </div>

        {master?.configured && parts.length > 0 && (
          <div className="mt-5" data-testid="master-editor">
            {missingDuty > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-md mb-3"
                   style={{ background: "#FEF3C7", border: "1px solid #FCD34D" }}>
                <AlertCircle size={18} className="text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <span className="font-semibold">{missingDuty} part(s) have no Duty per Unit.</span> Enter values below, or use the bulk tariff helper.
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-end gap-3 mb-3 p-3 rounded-md" style={{ background: "#F8F9FC", border: "1px solid var(--color-border)" }}>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                  Bulk apply tariff %
                </label>
                <input
                  data-testid="bulk-tariff-input"
                  type="number" step="0.0001"
                  value={bulkTariff}
                  onChange={(e) => setBulkTariff(e.target.value)}
                  placeholder="e.g., 53.9714"
                  className="w-44 px-3 py-2 rounded-md text-sm"
                  style={{ border: "1px solid var(--color-border)", background: "white" }}
                />
              </div>
              <button
                data-testid="apply-tariff-btn"
                onClick={applyTariff}
                className="bg-[#0F2044] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#1a2f5c] transition-colors"
              >
                Apply to all parts
              </button>
              <div className="text-xs text-gray-500 max-w-md">
                Computes <span className="font-mono">duty_per_unit = (unit_price + freight) × tariff%</span> for every part. Overwrites existing values.
              </div>
            </div>

            <div className="overflow-x-auto rounded-md" style={{ border: "1px solid var(--color-border)" }}>
              <table className="dr-table w-full text-sm" data-testid="master-parts-table">
                <thead>
                  <tr>
                    <th>Part #</th>
                    <th>HTS</th>
                    <th>Description</th>
                    <th className="text-right">Unit Price</th>
                    <th className="text-right">Freight/Unit</th>
                    <th className="text-right">Tariff %</th>
                    <th className="text-right">Duty / Unit (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {parts.map((p, i) => {
                    const dpu = Number(p.duty_per_unit) || 0;
                    return (
                      <tr key={`${p.part_number}-${i}`}>
                        <td className="font-mono text-xs">{p.part_number}</td>
                        <td className="font-mono text-xs">
                          <input
                            value={p.hts_code || ""}
                            onChange={(e) => editPart(i, "hts_code", e.target.value)}
                            className="w-28 px-2 py-1 rounded text-xs font-mono"
                            style={{ border: "1px solid var(--color-border)" }}
                          />
                        </td>
                        <td className="text-xs">{p.description || "—"}</td>
                        <td className="num">
                          <input
                            type="number" step="0.01"
                            value={p.unit_price ?? ""}
                            onChange={(e) => editPart(i, "unit_price", e.target.value)}
                            className="w-24 px-2 py-1 rounded text-xs text-right num"
                            style={{ border: "1px solid var(--color-border)" }}
                          />
                        </td>
                        <td className="num">
                          <input
                            type="number" step="0.01"
                            value={p.freight_per_unit ?? ""}
                            onChange={(e) => editPart(i, "freight_per_unit", e.target.value)}
                            className="w-24 px-2 py-1 rounded text-xs text-right num"
                            style={{ border: "1px solid var(--color-border)" }}
                          />
                        </td>
                        <td className="num">
                          <input
                            type="number" step="0.0001"
                            value={p.tariff_pct ?? ""}
                            onChange={(e) => editPart(i, "tariff_pct", e.target.value)}
                            className="w-24 px-2 py-1 rounded text-xs text-right num"
                            style={{ border: "1px solid var(--color-border)" }}
                          />
                        </td>
                        <td className="num">
                          <input
                            data-testid={`dpu-${i}`}
                            type="number" step="0.0001"
                            value={p.duty_per_unit ?? ""}
                            onChange={(e) => editPart(i, "duty_per_unit", e.target.value)}
                            className={`w-28 px-2 py-1 rounded text-xs text-right num font-semibold ${dpu > 0 ? "" : "bg-amber-50"}`}
                            style={{ border: "1px solid " + (dpu > 0 ? "var(--color-border)" : "#FCD34D") }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-3">
              <div className="text-xs text-gray-500">
                {parts.length} parts · {dirty ? <span className="text-amber-600 font-semibold">Unsaved changes</span> : "Saved"}
              </div>
              <div className="flex gap-2">
                {dirty && (
                  <button
                    onClick={() => setEditedParts(null)}
                    className="px-3 py-2 rounded-md text-sm bg-white hover:bg-slate-50"
                    style={{ border: "1px solid var(--color-border)" }}
                  >
                    Cancel
                  </button>
                )}
                <button
                  data-testid="save-parts-btn"
                  onClick={saveParts}
                  disabled={!dirty || savingParts}
                  className="bg-[#0F2044] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#1a2f5c] transition-colors disabled:opacity-50"
                >
                  {savingParts ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Thresholds */}
      <div className="bg-white rounded-md p-5 mb-6" style={{ border: "1px solid var(--color-border)" }} data-testid="thresholds-card">
        <div className="text-xs uppercase tracking-wider font-semibold text-[#0F2044] mb-3">
          Variance thresholds
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Match %</label>
            <input
              data-testid="match-pct"
              type="number" step="0.01"
              value={settings.match_pct}
              onChange={(e) => setSettings({ ...settings, match_pct: parseFloat(e.target.value || "0") })}
              className="w-full px-3 py-2 rounded-md text-sm" style={{ border: "1px solid var(--color-border)" }}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Minor variance %</label>
            <input
              data-testid="minor-pct"
              type="number" step="0.01"
              value={settings.minor_pct}
              onChange={(e) => setSettings({ ...settings, minor_pct: parseFloat(e.target.value || "0") })}
              className="w-full px-3 py-2 rounded-md text-sm" style={{ border: "1px solid var(--color-border)" }}
            />
          </div>
          <button
            data-testid="save-thresholds"
            onClick={saveThresholds}
            className="bg-[#0F2044] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#1a2f5c] transition-colors"
          >
            Save thresholds
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          |Variance %| &lt; {settings.match_pct}% = MATCH · &lt; {settings.minor_pct}% = MINOR · ≥ {settings.minor_pct}% = MATERIAL
        </p>
      </div>

      {/* Users */}
      <div className="bg-white rounded-md p-5 mb-6" style={{ border: "1px solid var(--color-border)" }}>
        <div className="text-xs uppercase tracking-wider font-semibold text-[#0F2044] mb-3">
          Add user
        </div>
        <form onSubmit={addUser} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end" data-testid="add-user-form">
          <input data-testid="new-username" placeholder="Username" required
                 value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                 className="px-3 py-2 rounded-md text-sm" style={{ border: "1px solid var(--color-border)" }} />
          <input data-testid="new-fullname" placeholder="Full name"
                 value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                 className="px-3 py-2 rounded-md text-sm" style={{ border: "1px solid var(--color-border)" }} />
          <input data-testid="new-password" placeholder="Password" type="password" required
                 value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                 className="px-3 py-2 rounded-md text-sm" style={{ border: "1px solid var(--color-border)" }} />
          <select data-testid="new-role"
                  value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="px-3 py-2 rounded-md text-sm bg-white" style={{ border: "1px solid var(--color-border)" }}>
            <option value="admin">Admin</option>
            <option value="finance">Finance</option>
            <option value="viewer">Viewer</option>
          </select>
          <button data-testid="add-user-submit" type="submit"
                  className="bg-[#0F2044] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#1a2f5c] transition-colors">
            Add user
          </button>
        </form>
      </div>

      <div className="bg-white rounded-md overflow-hidden" style={{ border: "1px solid var(--color-border)" }} data-testid="users-table">
        <div className="px-4 py-3 border-b font-semibold text-[#0F2044] text-sm uppercase tracking-wider" style={{ borderColor: "var(--color-border)" }}>
          Users
        </div>
        <div className="overflow-x-auto">
          <table className="dr-table w-full text-sm">
            <thead>
              <tr>
                <th>Username</th>
                <th>Full name</th>
                <th>Role</th>
                <th>Created</th>
                <th>Active</th>
                <th>Reset password</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="font-mono text-xs">{u.username}</td>
                  <td>{u.full_name}</td>
                  <td><span className="text-xs uppercase font-semibold">{u.role}</span></td>
                  <td className="uppercase-date">{ddmmmyy(u.created_at)}</td>
                  <td>
                    <span className={`text-xs font-semibold ${u.active ? "text-emerald-600" : "text-red-600"}`}>
                      {u.active ? "ACTIVE" : "DISABLED"}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <input
                        type="password" placeholder="New pw"
                        value={resetPw[u.id] || ""}
                        onChange={(e) => setResetPw({ ...resetPw, [u.id]: e.target.value })}
                        className="px-2 py-1 rounded-md text-xs"
                        style={{ border: "1px solid var(--color-border)" }}
                      />
                      <button
                        onClick={() => doReset(u.id)}
                        className="px-2 py-1 rounded-md text-xs bg-slate-100 hover:bg-slate-200"
                      >Reset</button>
                    </div>
                  </td>
                  <td>
                    <button
                      data-testid={`toggle-${u.username}`}
                      onClick={() => toggleUser(u.id)}
                      className={`px-3 py-1 rounded-md text-xs font-semibold ${u.active ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}
                    >
                      {u.active ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Settings;
